import CurrencyFormatter from "currency-formatter"
import GeneralHelper from "../../utils/GeneralHelper";



export class SellingExpenses {
  /**
   *
   * @param {number} brokerageFees
   * @param {number} filingsAndRecordings
   * @param {number} attorneyFees
   * @param {number} accountantFees
   * @param {number} escrowFees
   * @param {array<CustomField>} customFields
   */
  constructor({
    brokerageFees,
    filingsAndRecordings,
    attorneyFees,
    accountantFees,
    escrowFees,
    customFields = []
  }) {
    this.brokerageFees = brokerageFees
    this.filingsAndRecordings = filingsAndRecordings
    this.attorneyFees = attorneyFees
    this.accountantFees = accountantFees
    this.escrowFees = escrowFees
    this.customFields = customFields
  }

  get brokerageFeesFloat() {
    return CurrencyFormatter.unformat(this.brokerageFees, { code: "USD" })
  }

  get filingsAndRecordingsFloat() {
    return CurrencyFormatter.unformat(this.filingsAndRecordings, { code: "USD" })
  }

  get attorneyFeesFloat() {
    return CurrencyFormatter.unformat(this.attorneyFees, { code: "USD" })
  }

  get accountantFeesFloat() {
    return CurrencyFormatter.unformat(this.accountantFees, { code: "USD" })
  }

  get escrowFeesFloat() {
    return CurrencyFormatter.unformat(this.escrowFees, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalSellingExpenses() {
    return (
      this.brokerageFeesFloat
      + this.filingsAndRecordingsFloat
      + this.attorneyFeesFloat
      + this.accountantFeesFloat
      + this.escrowFeesFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalSellingExpensesFormatted() {
    return CurrencyFormatter.format(this.totalSellingExpenses, { code: "USD", symbol: "$" })
  }
}

export class AccountsPayable {
  /**
   * @param {number} payrollPayable
   * @param {number} salesTaxPayable
   * @param {array<CustomField>} customFields
   */
  constructor({
    payrollPayables,
    salesTaxPayables,
    customFields = []
  }) {
    this.payrollPayables = payrollPayables
    this.salesTaxPayables = salesTaxPayables
    this.customFields = customFields
  }

  get salesTaxPayablesFloat() {
    return CurrencyFormatter.unformat(this.salesTaxPayables, { code: "USD" })
  }

  get payrollPayablesFloat() {
    return CurrencyFormatter.unformat(this.payrollPayables, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalAccountsPayable() {
    return (
      this.salesTaxPayablesFloat + this.payrollPayablesFloat + this.totalCustomFieldsAmount
    )
  }

  get totalAccountsPayableFormatted() {
    return CurrencyFormatter.format(this.totalAccountsPayable, { code: "USD", symbol: "$" })
  }
}

export class OtherExpensesNeededToClose {
  /**
   *
   * @param {number} equipmentRepair
   * @param {number} equipmentReplace
   * @param {number} complianceWithGovernmentalAgencies
   * @param {array<CustomField>} customFields
   */
  constructor({
    equipmentRepair = 0,
    equipmentReplace = 0,
    complianceWithGovernmentalAgencies = 0,
    customFields = []
  }) {
    this.equipmentRepair = equipmentRepair
    this.equipmentReplace = equipmentReplace
    this.complianceWithGovernmentalAgencies = complianceWithGovernmentalAgencies
    this.customFields = customFields
  }

  get equipmentRepairFloat() {
    return CurrencyFormatter.unformat(this.equipmentRepair, { code: "USD" })
  }

  get equipmentReplaceFloat() {
    return CurrencyFormatter.unformat(this.equipmentReplace, { code: "USD" })
  }

  get equipmentComplianceWithGovernmentalAgenciesFloat() {
    return CurrencyFormatter.unformat(this.complianceWithGovernmentalAgencies, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalOtherExpensesNeededToClose() {
    return (
      this.equipmentRepairFloat
      + this.equipmentReplaceFloat
      + this.equipmentComplianceWithGovernmentalAgenciesFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalOtherExpensesNeededToCloseFormatted() {
    return CurrencyFormatter.format(this.totalOtherExpensesNeededToClose, { code: "USD", symbol: "$" })
  }
}

export class CurrentAssetsKeptBySeller {
  /**
   *
   * @param {array<FinanceAccount>}currentAssets
   * @param {number} returnOfLeaseDeposit
   * @param {number} pettyCash
   * @param {number} proratedRent
   * @param {number} utilityDeposit
   * @param {number} proratedTaxes
   * @param {number} rebateForPrepaidExpenses
   * @param {array<CustomField>} customFields
   */
  constructor({
    currentAssets = [],
    returnOfLeaseDeposit,
    pettyCash,
    proratedRent,
    utilityDeposit,
    proratedTaxes,
    rebateForPrepaidExpenses,
    customFields = []
  }) {
    this.currentAssets = currentAssets
    this.returnOfLeaseDeposit = returnOfLeaseDeposit
    this.pettyCash = pettyCash
    this.proratedRent = proratedRent
    this.utilityDeposit = utilityDeposit
    this.proratedTaxes = proratedTaxes
    this.rebateForPrepaidExpenses = rebateForPrepaidExpenses
    this.customFields = customFields
  }

  get returnOfLeaseDepositFloat() {
    return CurrencyFormatter.unformat(this.returnOfLeaseDeposit, { code: "USD" })
  }

  get pettyCashFloat() {
    return CurrencyFormatter.unformat(this.pettyCash, { code: "USD" })
  }

  get proratedRentFloat() {
    return CurrencyFormatter.unformat(this.proratedRent, { code: "USD" })
  }

  get utilityDepositFloat() {
    return CurrencyFormatter.unformat(this.utilityDeposit, { code: "USD" })
  }

  get proratedTaxesFloat() {
    return CurrencyFormatter.unformat(this.proratedTaxes, { code: "USD" })
  }

  get rebateForPrepaidExpensesFloat() {
    return CurrencyFormatter.unformat(this.rebateForPrepaidExpenses, { code: "USD" })
  }

  get totalCustomFieldsAmount(){
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalCurrentAssetsKeptBySeller() {
    const currentAssetsSum = GeneralHelper.sumObjectAttributeFromArray(this.currentAssets, "amount")
    return (
      currentAssetsSum
      + this.returnOfLeaseDepositFloat
      + this.pettyCashFloat
      + this.proratedRentFloat
      + this.utilityDepositFloat
      + this.proratedTaxesFloat
      + this.rebateForPrepaidExpensesFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalCurrentAssetsKeptBySellerFormatted() {
    return CurrencyFormatter.format(this.totalCurrentAssetsKeptBySeller, { code: "USD", symbol: "$" })
  }
}

export class LiabilitiesAssumedByBuyer {
  /**
   * @param {array<FinanceAccount>} liabilitiesAssumedByBuyer
   * @param {number} customerPrepayments
   * @param {number} vacationPayable
   * @param {array<CustomField>} customFields
   */
  constructor({
    liabilitiesAssumedByBuyer = [],
    customerPrepayments,
    vacationPayable,
    customFields = []
  }) {
    this.liabilitiesAssumedByBuyer = liabilitiesAssumedByBuyer
    this.customerPrepayments = customerPrepayments
    this.vacationPayable = vacationPayable
    this.customFields = customFields
  }

  get customerPrepaymentsFloat() {
    return CurrencyFormatter.unformat(this.customerPrepayments, { code: "USD" })
  }

  get vacationPayableFloat() {
    return CurrencyFormatter.unformat(this.vacationPayable, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalLiabilitiesAssumedByBuyer() {
    const liabilitiesAssumedByBuyerSum = GeneralHelper.sumObjectAttributeFromArray(this.liabilitiesAssumedByBuyer, "amount")
    return (
      liabilitiesAssumedByBuyerSum
      + this.customerPrepaymentsFloat
      + this.vacationPayableFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalLiabilitiesAssumedByBuyerFormatted() {
    return CurrencyFormatter.format(this.totalLiabilitiesAssumedByBuyer, { code: "USD", symbol: "$" })
  }
}

export class Financing {
  /**
   *
   * @param {number} financingPrinciple
   * @param {number} interestOwed
   * @param {array<CustomField>} customFields
   */
  constructor({
    financingPrinciple,
    interestOwed,
    customFields = []
  }) {
    this.financingPrinciple = financingPrinciple
    this.interestOwed = interestOwed
    this.customFields = customFields
  }

  get financingPrincipleFormatted() {
    return CurrencyFormatter.format(this.financingPrinciple, { code: "USD", symbol: "$" })
  }

  get interestOwedFormatted() {
    return CurrencyFormatter.format(this.interestOwed, { code: "USD", symbol: "$" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalFinancing() {
    return this.financingPrinciple + this.interestOwed + this.totalCustomFieldsAmount
  }

  get totalFinancingFormatted() {
    return CurrencyFormatter.format(this.totalFinancing, { code: "USD", symbol: "$" })
  }
}

export class LongTermAssetsKeptBySeller {
  constructor({
    longTermAssets = [],
    customFields = []
  }) {
    this.longTermAssets = longTermAssets
    this.customFields = customFields
  }

  get totalLongTermAssetsKeptBySeller() {
    return (
      GeneralHelper.sumObjectAttributeFromArray(this.longTermAssets, "amount")
      + GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
    )
  }

  get totalLongTermAssetsKeptBySellerFormatted() {
    return CurrencyFormatter.format(this.totalLongTermAssetsKeptBySeller, { code: "USD", symbol: "$" })
  }

  get hasItems() {
    return this.longTermAssets?.length > 0 || this.customFields?.length > 0
  }
}

export class LongTermDebtNotAssumedByBuyer {
  /**
   *
   * @param {number} longTermDebts
   * @param {array} customFields
   */
  constructor({
    longTermDebt = [],
    customFields = []
  }) {
    this.longTermDebt = longTermDebt
    this.customFields = customFields
  }

  get totalLongTermDebt() {
    return (
      GeneralHelper.sumObjectAttributeFromArray(this.longTermDebt, "amount")
      + GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
    )
  }

  get totalLongTermDebtFormatted() {
    return CurrencyFormatter.format(this.totalLongTermDebt, { code: "USD", symbol: "$" })
  }

  get hasItems() {
    return this.longTermDebt.length > 0 || this.customFields.length > 0
  }
}


export default class OwnerFinancingStockSaleReport {
  /**
   *
   * @param {number} cashDownPayment
   * @param {SellingExpenses} sellingExpenses
   * @param {AccountsPayable} accountsPayable
   * @param {LongTermDebtNotAssumedByBuyer} longTermDebtNotAssumedByBuyer
   * @param {OtherExpensesNeededToClose} otherExpensesNeededToClose
   * @param {CurrentAssetsKeptBySeller} totalCurrentAssetsKeptBySeller
   * @param {number} totalSellingPrice
   * @param {LiabilitiesAssumedByBuyer} existingLiabilitiesAssumedByBuyer
   * @param {LongTermAssetsKeptBySeller} longTermAssetsKeptBySeller
   * @param {number} excessBalanceSheetAssets
   * @param {number} totalInterest
   * @param {Financing} financing
   * @param {CustomField} customFieldToAdd
   * @param {CustomField} customFieldToSubtract
   */
  constructor({
    cashDownPayment,
    sellingExpenses,
    accountsPayable,
    longTermDebtNotAssumedByBuyer,
    otherExpensesNeededToClose,
    totalCurrentAssetsKeptBySeller,
    totalSellingPrice,
    existingLiabilitiesAssumedByBuyer,
    longTermAssetsKeptBySeller,
    excessBalanceSheetAssets,
    financing,
    totalInterest,
    customFieldToAdd,
    customFieldToSubtract
  }) {
    this.cashDownPayment = cashDownPayment
    this.sellingExpenses = sellingExpenses
    this.accountsPayable = accountsPayable
    this.longTermDebtNotAssumedByBuyer = longTermDebtNotAssumedByBuyer
    this.otherExpensesNeededToClose = otherExpensesNeededToClose
    this.totalCurrentAssetsKeptBySeller = totalCurrentAssetsKeptBySeller
    this.totalSellingPrice = totalSellingPrice
    this.existingLiabilitiesAssumedByBuyer = existingLiabilitiesAssumedByBuyer
    this.longTermAssetsKeptBySeller = longTermAssetsKeptBySeller
    this.excessBalanceSheetAssets = excessBalanceSheetAssets
    this.financing = financing
    this.totalInterest = totalInterest
    this.customFieldToAdd = customFieldToAdd
    this.customFieldToSubtract = customFieldToSubtract
  }

  // Float values

  get totalSalesAndOtherExpenses() {
    return (
      this.sellingExpenses.totalSellingExpenses
      + this.accountsPayable.totalAccountsPayable
      + this.longTermDebtNotAssumedByBuyer.totalLongTermDebt
      + this.otherExpensesNeededToClose.totalOtherExpensesNeededToClose
    )
  }

  get netCashAfterLiabilitiesAndSellingExpenses() {
    return this.cashDownPayment - this.totalSalesAndOtherExpenses
  }

  get estimatedTotalNetCashProceedsToSeller() {
    return (
      this.cashDownPayment
      - this.totalSalesAndOtherExpenses
      + this.totalCurrentAssetsKeptBySeller.totalCurrentAssetsKeptBySeller
    )
  }

  get balanceDueSeller() {
    return (
      this.totalSellingPrice
      - (this.cashDownPayment + this.existingLiabilitiesAssumedByBuyer.totalLiabilitiesAssumedByBuyer)
    )
  }

  get valueOfTotalAssetsAtClosingForSeller() {
    return this.balanceDueSeller + this.longTermAssetsKeptBySeller?.totalLongTermAssetsKeptBySeller + this.excessBalanceSheetAssets
  }

  get totalSellerCompensation() {
    let sellingPrice = this.valueOfTotalAssetsAtClosingForSeller + this.financing.totalFinancing

    if (this.customFieldToSubtract && this.customFieldToSubtract?.value) {
      sellingPrice -= this.customFieldToSubtract.value
    }

    if (this.customFieldToAdd && this.customFieldToAdd?.value) {
      sellingPrice += this.customFieldToAdd?.value
    }

    return sellingPrice
  }

  get netPresentAndFutureCashAndRetainedAssetsAfterAdjustmentsForExcess() {
    return (
      this.netPresentAndFutureCashAndRetainedAssetsToSeller
      + (this.customFieldToAdd?.value || 0)
      - (this.customFieldToSubtract?.value || 0)
    )
  }

  // Formatted

  get cashDownPaymentFormatted() {
    return CurrencyFormatter.format(this.cashDownPayment, { code: "USD", symbol: "$" })
  }

  get totalSalesAndOtherExpensesFormatted() {
    return CurrencyFormatter.format(this.totalSalesAndOtherExpenses, { code: "USD", symbol: "$" })
  }

  get netCashAfterLiabilitiesAndSellingExpensesFormatted() {
    return CurrencyFormatter.format(this.netCashAfterLiabilitiesAndSellingExpenses, { code: "USD", symbol: "$" })
  }

  get estimatedTotalNetCashProceedsToSellerFormatted() {
    return CurrencyFormatter.format(this.estimatedTotalNetCashProceedsToSeller, { code: "USD", symbol: "$" })
  }

  get excessBalanceSheetAssetsFormatted() {
    return CurrencyFormatter.format(this.excessBalanceSheetAssets, { code: "USD", symbol: "$" })
  }

  get balanceDueSellerFormatted() {
    return CurrencyFormatter.format(this.balanceDueSeller, { code: "USD", symbol: "$" })
  }

  get valueOfTotalAssetsAtClosingForSellerFormatted() {
    return CurrencyFormatter.format(this.valueOfTotalAssetsAtClosingForSeller, { code: "USD", symbol: "$" })
  }

  get totalSellerCompensationFormatted() {
    return CurrencyFormatter.format(this.totalSellerCompensation, { code: "USD", symbol: "$" })
  }

  get totalSellingPriceFormatted() {
    return CurrencyFormatter.format(this.totalSellingPrice, { code: "USD", symbol: "$" })
  }

  get remainingBalanceOfPurchasePrice() {
    return this.totalSellingPrice - this.cashDownPayment
  }

  get totalInterestOnSellerCarryBackNote() {
    return this.totalInterest
  }

  get totalMoneyOwnedToTheSeller() {
    return (
      this.cashDownPayment
      + this.remainingBalanceOfPurchasePrice
      - this.existingLiabilitiesAssumedByBuyer?.totalLiabilitiesAssumedByBuyer
    )
  }

  get estimatedSellerRetainedAssets() {
    return (
      this.longTermAssetsKeptBySeller?.totalLongTermAssetsKeptBySeller
      + this.totalCurrentAssetsKeptBySeller?.totalCurrentAssetsKeptBySeller
      + this.excessBalanceSheetAssets
    )
  }

  get grossPresentAndFutureCashAndRetainedAssetsToSeller() {
    return (
      this.totalMoneyOwnedToTheSeller
      + this.estimatedSellerRetainedAssets
      + this.totalSalesAndOtherExpenses
      + this.totalInterestOnSellerCarryBackNote
    )
  }


  get netPresentAndFutureCashAndRetainedAssetsToSeller() {
    return this.grossPresentAndFutureCashAndRetainedAssetsToSeller - this.longTermDebtNotAssumedByBuyer?.totalLongTermDebt
  }

  get proceedsSummary() {
    return {
      downPayment: this.cashDownPaymentFormatted,
      remainingBalanceOfPurchasePrice: CurrencyFormatter.format(this.remainingBalanceOfPurchasePrice, { code: "USD" }),
      totalInterestOnSellerCarryBackNote: CurrencyFormatter.format(this.totalInterestOnSellerCarryBackNote, { code: "USD" }),
      totalMoneyOwnedToTheSeller: CurrencyFormatter.format(this.totalMoneyOwnedToTheSeller, { code: "USD" }),
      estimatedSellerRetainedAssets: CurrencyFormatter.format(this.estimatedSellerRetainedAssets, { code: "USD" }),
      lessTotalExpensesTheSellerToPayAtClosing: CurrencyFormatter.format(this.totalSalesAndOtherExpenses, { code: "USD" }),
      grossPresentAndFutureCashAndRetainedAssetsToSeller: CurrencyFormatter.format(this.grossPresentAndFutureCashAndRetainedAssetsToSeller, { code: "USD" }),
      lessInterestBearingDebt: CurrencyFormatter.format(this.longTermDebtNotAssumedByBuyer?.totalLongTermDebt, { code: "USD" }),
      netPresentAndFutureCashAndRetainedAssetsToSeller: CurrencyFormatter.format(this.netPresentAndFutureCashAndRetainedAssetsToSeller, { code: "USD" }),
      netPresentAndFutureCashAndRetainedAssetsAfterAdjustmentsForExcess: CurrencyFormatter.format(this.netPresentAndFutureCashAndRetainedAssetsAfterAdjustmentsForExcess, { code: "USD" })
    }
  }
}